import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { Plus, Ship } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SystemShipOverviewRow } from '@/core/api';
import { useSystemShipsOverview } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  Badge,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { cn, getSystemShipDetailHref } from '@/shared/utils';
import {
  canCreateService,
  resolveUserGroup,
  resolveUserGroupDomain,
} from '@/shared/utils/permissions';

type ShipOverviewStatus = 'active' | 'inactive' | 'archived';

export function SystemShipsOverview({
  categoryFilter,
  filterForCurrentUserCustomer = false,
}: {
  categoryFilter: string;
  filterForCurrentUserCustomer?: boolean;
}) {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user } = useAuth();
  const group = resolveUserGroup(user);
  const domain = resolveUserGroupDomain(user);
  const tablePreferences = useTablePreferences({
    tableId: `system-ships-overview-${domain}-${user?.id ?? 'anonymous'}`,
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      searchTerm: '',
      selectedFilters: {
        overviewStatus: 'active',
      },
    },
  });
  const preferences = tablePreferences.loadPreferences();
  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const [overviewStatus, setOverviewStatus] = useState<ShipOverviewStatus>(
    preferences.selectedFilters?.overviewStatus === 'inactive'
      ? 'inactive'
      : preferences.selectedFilters?.overviewStatus === 'archived' ||
          preferences.selectedFilters?.archiveStatus === 'archived'
        ? 'archived'
        : 'active'
  );
  const [pagination, setPagination] = useState<PaginationState>(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 20,
    }
  );
  const { data, isLoading } = useSystemShipsOverview({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    searchTerm,
    overviewStatus: domain === 'customer' ? undefined : overviewStatus,
  });

  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      searchTerm,
      selectedFilters: {
        overviewStatus,
      },
    });
  }, [overviewStatus, pagination, searchTerm, tablePreferences]);

  const canCreateCustomerShip =
    domain === 'customer' &&
    (group?.key === 'customer-admin' ||
      group?.key === 'customer-user' ||
      group?.key === 'heli-provider');
  const canCreate = canCreateService(group, 'objects') || canCreateCustomerShip;

  const createHref =
    domain === 'customer'
      ? `/c/objects/new?category=${categoryFilter}`
      : `/gmn/objects/new?category=${categoryFilter}`;

  const detailBaseData = useMemo(
    () => ({
      domain,
      canReadObjects: true,
    }),
    [domain]
  );

  const title = filterForCurrentUserCustomer
    ? t('objects.ships.overviewTitleCustomer')
    : t('objects.ships.overviewTitle');
  const canViewArchivedShips = domain !== 'customer';
  const primaryActionLabel =
    domain === 'customer'
      ? t('objects.ships.createAssignmentCustomer')
      : t('objects.ships.createAssignment');

  const columns = useMemo<ColumnDef<SystemShipOverviewRow>[]>(() => {
    const baseColumns: ColumnDef<SystemShipOverviewRow>[] = [
      {
        accessorKey: 'name',
        header: () => t('common.name'),
        cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
        size: 260,
      },
      {
        accessorKey: 'imo',
        header: () => 'IMO',
        cell: ({ row }) => row.original.imo || '-',
        size: 140,
      },
      {
        accessorKey: 'flag',
        header: () => t('objects.masterData'),
        cell: ({ row }) => row.original.flag || '-',
        size: 160,
      },
      {
        accessorKey: 'objectCount',
        header: () => t('objects.ships.assignmentRecords'),
        cell: ({ row }) => row.original.objectCount,
        size: 160,
      },
      {
        accessorKey: 'active',
        header: () => t('common.status'),
        cell: ({ row }) => (
          <Badge variant={row.original.active ? 'default' : 'secondary'}>
            {row.original.active ? t('objects.ships.activeToday') : t('objects.ships.inactive')}
          </Badge>
        ),
        size: 160,
      },
    ];

    if (domain !== 'customer') {
      baseColumns.splice(3, 0, {
        id: 'currentCustomer',
        header: () => t('customers.customer'),
        cell: ({ row }) => row.original.currentCustomer?.name || '-',
        size: 220,
      });
    }

    return baseColumns;
  }, [detailBaseData, domain, t]);

  const rows = data?.docs ?? [];
  const table = useReactTable({
    data: rows,
    columns,
    pageCount: data?.totalPages ?? 0,
    rowCount: data?.totalDocs ?? 0,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <PageContainer>
      <PageTitle
        icon={Ship}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{t('objects.objects')}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{title}</SimpleBreadcrumbItem>
          </>
        }
        title={title}
        primaryAction={{
          label: primaryActionLabel,
          onClick: () => navigate(createHref),
          icon: <Plus size={16} />,
          disabled: !canCreate,
        }}
      />

      <TableCard className="mb-4">
        <TableCardHeader>
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <TableSearchBar
                searchTerm={searchTerm}
                onSearchChange={(term) => {
                  setPagination((value) => ({ ...value, pageIndex: 0 }));
                  setSearchTerm(term);
                }}
                placeholder={t('objects.ships.searchPlaceholder')}
              />
            </div>
            {canViewArchivedShips ? (
              <Tabs
                value={overviewStatus}
                onValueChange={(value) => {
                  if (value !== 'active' && value !== 'inactive' && value !== 'archived') return;
                  setPagination((current) => ({ ...current, pageIndex: 0 }));
                  setOverviewStatus(value);
                }}
                className="w-full shrink-0 md:w-auto"
              >
                <TabsList className="grid w-full grid-cols-3 md:inline-flex md:w-auto">
                  <TabsTrigger value="active">{t('objects.ships.activeShipsTab')}</TabsTrigger>
                  <TabsTrigger value="inactive">{t('objects.ships.inactiveShipsTab')}</TabsTrigger>
                  <TabsTrigger value="archived">{t('objects.ships.archivedShipsTab')}</TabsTrigger>
                </TabsList>
              </Tabs>
            ) : null}
          </div>
        </TableCardHeader>
      </TableCard>

      <TableCard className="mb-6">
        <TableCardContent>
          <div className="overflow-x-auto">
            <Table className="min-w-max">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableHeaderRow key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      const isStickyLeft = header.column.id === 'name';

                      return (
                        <TableHead
                          key={header.id}
                          style={{ width: `${header.getSize()}px` }}
                          className={cn(
                            index === 0 ? 'pl-6' : '',
                            index === headerGroup.headers.length - 1 ? 'pr-6 text-right' : '',
                            isStickyLeft &&
                              'sticky left-0 z-30 bg-muted shadow-[inset_-1px_0_0_hsl(var(--border)),4px_0_12px_-4px_rgba(0,0,0,0.08)]'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableHeaderRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => {
                    const href = getSystemShipDetailHref({
                      systemShipId: row.original.id,
                      ...detailBaseData,
                    });

                    return (
                      <TableRow
                        key={row.id}
                        className={cn(href && 'cursor-pointer hover:bg-muted')}
                        onClick={() => {
                          if (href) navigate(href);
                        }}
                      >
                        {row.getVisibleCells().map((cell, index) => {
                          const isStickyLeft = cell.column.id === 'name';

                          return (
                            <TableCell
                              key={cell.id}
                              style={{ width: `${cell.column.getSize()}px` }}
                              className={cn(
                                index === 0 ? 'pl-6' : '',
                                index === row.getVisibleCells().length - 1 ? 'pr-6' : '',
                                isStickyLeft &&
                                  'sticky left-0 z-20 bg-card group-hover:bg-card shadow-[inset_-1px_0_0_hsl(var(--border)),4px_0_12px_-4px_rgba(0,0,0,0.08)]'
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                ) : !isLoading ? (
                  <EmptyTableRow colSpan={columns.length} />
                ) : (
                  <TableLoadingCell colSpan={columns.length} />
                )}
              </TableBody>
            </Table>
          </div>
        </TableCardContent>

        <TableCardFooter>
          <TablePagination table={table} />
        </TableCardFooter>
      </TableCard>
    </PageContainer>
  );
}
